import { ApplicationController, ControlledComponent } from '@aha-app/mvc';
import React from 'react';
import { z } from 'zod';
import WidgetsController from '../../../../controllers/WidgetsController';
import { hasVotingBehaviors } from '../../../../objects/guards';
import type Shape from '../../../../objects/Shape';
import type { SidePanelItem } from '../../../../shared-types';
import type { RecordId } from '../../../../types';
import {
  durations,
  type DurationUnit,
  MINUTE,
  shortFormattedTime,
  timeInMillis,
} from '../../../../utils/time';
import { zoomAndPanTo } from '../../../../utils/zoom';
import type { SerializedVotingSessionWidget } from '../../../../widgets';
import type VotingSessionWidget from '../../../../widgets/VotingSessionWidget';
import { VotingSessionStatus } from '../../../../widgets/VotingSessionWidget';
import type SidePanelController from '../../../SidePanel/controllers/SidePanelController';
import ResultsPanel from '../ResultsPanel';
import { VotingSessionType } from '../../../../widgets/VotingSessionType';

const zLocalStorageSettings = z.object({
  sessionType: z.nativeEnum(VotingSessionType),
  pokerPreset: z.enum(['fibonacci', 'tshirt', 'custom']).optional(),
  pokerChoices: z.array(z.string()).optional(),
});

export type PokerPreset = Exclude<
  z.infer<typeof zLocalStorageSettings>['pokerPreset'],
  undefined
>;

export type PokerChoices = Exclude<
  z.infer<typeof zLocalStorageSettings>['pokerChoices'],
  undefined
>;

export const pokerDefaultChoices: Record<PokerPreset, string[]> = {
  fibonacci: ['1', '2', '3', '5', '8', '13', '21'],
  tshirt: ['XS', 'S', 'M', 'L', 'XL'],
  custom: [],
};

interface State {
  widget?: VotingSessionWidget;
  name?: string;
  votesPerPerson: number;
  votesLimited: boolean;
  durationInSeconds: number;
  timeLimited: boolean;
  remainingTime: number;
  completingAt: number;
  onlySelected: boolean;
  selectedObjects: string[];
  sessionType: VotingSessionType;
  numObjects: number;
  pokerPreset: PokerPreset;
  pokerChoices: PokerChoices;
}

interface ControllerProps {
  widget?: VotingSessionWidget;
}

const localStorageLastSelectedVotingSessionType =
  'whiteboardsVotingSessionType';

const getLocalStorageSettings = () => {
  const localStorageValue = localStorage.getItem(
    localStorageLastSelectedVotingSessionType
  );
  if (!localStorageValue) return null;

  try {
    return zLocalStorageSettings.parse(JSON.parse(localStorageValue));
  } catch {
    return null;
  }
};

const defaultDurationInSeconds = 5 * MINUTE;

class VotingSessionController extends ApplicationController<
  State,
  ControllerProps
> {
  widgetsController: WidgetsController;

  static OBJECT_LIMIT = 300;

  static get initialState(): State {
    const localStorageSettings = getLocalStorageSettings();

    const sessionType =
      localStorageSettings?.sessionType ?? VotingSessionType.Standard;

    const pokerChoices =
      localStorageSettings?.pokerChoices ?? pokerDefaultChoices.fibonacci;

    return {
      name: undefined,
      widget: undefined,
      votesPerPerson:
        sessionType === VotingSessionType.Poker
          ? // In planning poker the user vote count is the index of the choice in pokerChoices.
            // e.g. if choices are ['S', 'M', 'L'] then a vote of 1 === 'M'
            pokerChoices.length
          : 3,
      votesLimited: true,
      durationInSeconds: defaultDurationInSeconds,
      timeLimited: sessionType !== VotingSessionType.Poker,
      remainingTime: 0,
      completingAt: Infinity,
      onlySelected: false,
      selectedObjects: [],
      sessionType,
      numObjects: 0,
      pokerPreset: localStorageSettings?.pokerPreset ?? 'fibonacci',
      pokerChoices,
    };
  }

  async initialize({ widget }: ControllerProps): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.widgetsController = this.findControllerInstance(WidgetsController)!;

    this.setWidget(widget);

    const canvas = this.widgetsController.whiteboardController.canvas;
    canvas.on({
      'selection:created': this.onSelectionUpdated,
      'selection:updated': this.onSelectionUpdated,
      'selection:cleared': this.onSelectionUpdated,
      'object:added': this.onSelectionUpdated,
      'object:removed': this.onSelectionUpdated,
    });
  }

  changeProps(newProps: ControllerProps): void {
    this.setWidget(newProps.widget);
  }

  destroy(): void {
    const canvas = this.widgetsController.whiteboardController.canvas;

    if (canvas) {
      canvas.off({
        'selection:created': this.onSelectionUpdated,
        'selection:updated': this.onSelectionUpdated,
        'selection:cleared': this.onSelectionUpdated,
        'object:added': this.onSelectionUpdated,
        'object:removed': this.onSelectionUpdated,
      });
    }
    this.state.widget?.removeEventListener('tick', this.updateRemainingTime);
    this.state.widget?.removeEventListener('finish', this.onTimeExpired);
  }

  setWidget(widget: VotingSessionWidget | undefined): void {
    const previousWidget = this.state.widget;

    if (previousWidget) {
      previousWidget.removeEventListener('tick', this.updateRemainingTime);
      previousWidget.removeEventListener('finish', this.onTimeExpired);
    }

    this.state.widget = widget;

    if (widget) {
      this.state.remainingTime = widget.remainingDuration;
      widget.addEventListener('tick', this.updateRemainingTime);
      widget.addEventListener('finish', this.onTimeExpired);
    }
  }

  get widgets() {
    return this.widgetsController.votingSessionWidgets;
  }

  get finishedWidgets() {
    return this.widgets.filter(widget => widget.isFinished);
  }

  get name(): string {
    return this.state.name ?? this.defaultName;
  }

  get defaultName(): string {
    const kindName =
      this.state.sessionType === VotingSessionType.Poker ? 'Poker' : 'Voting';
    const regexp = new RegExp(`^${kindName} session ?(\\d*)`);
    let match: RegExpMatchArray | null = null;

    for (const w of [...this.finishedWidgets].reverse()) {
      match = w.name.match(regexp);

      if (match) {
        break;
      }
    }

    if (match?.[1]) {
      return `${kindName} session ${parseInt(match[1], 10) + 1}`;
    } else if (match) {
      return `${kindName} session 2`;
    } else {
      return `${kindName} session`;
    }
  }

  get enabledFields() {
    switch (this.state.sessionType) {
      case VotingSessionType.Standard:
        return {
          votesLimited: true,
          onlySelected: true,
        };
      case VotingSessionType.FistToFive:
        return {
          votesLimited: false,
          onlySelected: false,
        };
      case VotingSessionType.Poker:
        return {
          votesLimited: false,
          onlySelected: false,
        };
      default: {
        const _exhaustivenessCheck: never = this.state.sessionType;
        return _exhaustivenessCheck;
      }
    }
  }

  getObject(objectId: string): fabric.Object | undefined {
    return this.widgetsController.whiteboardController.canvas
      .getObjects()
      .find(object => object.id === objectId);
  }

  getResolvedUser(userId?: RecordId): string {
    if (!userId) return '';

    const user =
      this.widgetsController.whiteboardController.getResolvedUser(userId);

    if (!user) {
      // Queue up a fetch for next time
      void this.widgetsController.whiteboardController.actionResolveUserInfo(
        userId
      );
    }

    return user?.displayName ?? '';
  }

  getCurrentVotingObject() {
    const { widget } = this.state;
    if (!widget) return null;
    return widget.currentVotingObject;
  }

  // When changing a duration unit, convert the numerical value to the
  // new unit. For example, "3 minutes" to hours will turn into "3 hours".
  private convertDuration(target: DurationUnit): void {
    let duration = this.state.durationInSeconds;
    const { unit } = shortFormattedTime(duration);

    duration = Math.floor(duration / durations[unit].factor);
    duration *= durations[target].factor;

    this.state.durationInSeconds = duration;
  }

  get userId(): RecordId | null {
    return this.widgetsController.whiteboardController.state.userId;
  }

  // The count of all potential voters. Uses a heuristic: the union of
  // all active cursors with all users who have participated in
  // voting.
  get voters(): { [key: RecordId]: boolean } {
    const voters = {};
    const { widget } = this.state;
    if (!widget) return voters;

    const { cursors, userId } =
      this.widgetsController.whiteboardController.state;

    // Current user
    if (userId) {
      voters[userId] = false;
    }

    // All other live cursors
    for (const cursor of Object.values(cursors)) {
      voters[cursor.userId] = false;
    }

    // Anyone already voted
    for (const voter of widget.voters) {
      switch (widget.sessionType) {
        // In fist-to-five if the user has voted then they have finished
        case VotingSessionType.FistToFive: {
          voters[voter] = true;
          break;
        }
        case VotingSessionType.Standard: {
          voters[voter] = widget.userFinishedVoting(voter);
          break;
        }
        case VotingSessionType.Poker: {
          voters[voter] = false;

          const currentObject = this.getCurrentVotingObject();
          if (!currentObject) {
            break;
          }

          const allVotes = widget.votesByObjectAndUser();
          const objectVotes = allVotes[currentObject.recordId];
          if (!objectVotes) {
            break;
          }
          const vote = objectVotes[voter];
          if (vote) {
            voters[voter] = true;
          }
          break;
        }
        default: {
          const _exhaustivenessCheck: never = widget.sessionType;
          return _exhaustivenessCheck;
        }
      }
    }

    return voters;
  }

  get votesRemaining(): number {
    if (!this.userId) return 0;

    return this.state.widget?.votesRemaining(this.userId) ?? 0;
  }

  get finishedVoting(): boolean {
    if (!this.userId) return false;

    return this.state.widget?.userFinishedVoting(this.userId) ?? false;
  }

  getVotesForObject(objectId: string): number {
    if (!this.userId) return 0;

    return this.state.widget?.userVotesForObject(objectId, this.userId) ?? 0;
  }

  onSelectionUpdated = () => {
    this.state.selectedObjects =
      this.widgetsController.whiteboardController.canvas
        .getActiveObjects()
        .filter(obj => hasVotingBehaviors(obj))
        .map(obj => obj.id);

    this.state.numObjects = this.widgetsController.whiteboardController.canvas
      .getObjects()
      .filter(obj => hasVotingBehaviors(obj)).length;

    if (this.state.numObjects > VotingSessionController.OBJECT_LIMIT) {
      // They must select specific objects
      this.state.onlySelected = true;
    }
  };

  updateRemainingTime = () => {
    this.state.remainingTime = this.state.widget?.remainingDuration ?? 0;

    if (this.state.widget) {
      this.state.completingAt =
        Date.now() + timeInMillis(this.state.widget.remainingDuration);
    }
  };

  onTimeExpired = () => {
    const { widget } = this.state;

    if (!widget) {
      return;
    }

    // Show results for the user who started the session if it ended
    // when we expected it to (nobody ended it early)
    if (
      widget.authorId === this.userId &&
      Date.now() >= this.state.completingAt
    ) {
      this.actionPushSidePanel({
        id: 'votingResults',
        name: widget.name,
        icon: 'aha-icon aha-icon-voting',
        panel: (
          <ControlledComponent controller={this}>
            <ResultsPanel widget={widget} />
          </ControlledComponent>
        ),
      });
    }

    this.setWidget(undefined);

    Object.keys(VotingSessionController.initialState).forEach(key => {
      this.state[key] = VotingSessionController.initialState[key];
    });
    this.widgetsController.whiteboardController.canvas.requestRenderAll();
  };

  actionShowObject(objectId: string): void {
    const object = this.getObject(objectId);
    if (!object) return;

    zoomAndPanTo(this.widgetsController.whiteboardController, [object], {
      maxZoom: 2,
      marginFactor: 0.6,
    });
  }

  actionSetName(name?: string): void {
    this.state.name = name;
  }

  actionSetVotesPerPerson(votesPerPerson: number): void {
    if (this.state.votesLimited && votesPerPerson > 0) {
      this.state.votesPerPerson = votesPerPerson;
    }
  }

  actionSetVotesLimited(limited: boolean): void {
    this.state.votesLimited = limited;
  }

  actionSetDurationUnit(unit: DurationUnit | null): void {
    if (unit) {
      this.convertDuration(unit);
      this.state.timeLimited = true;
    } else {
      this.state.timeLimited = false;
    }
  }

  actionSetDuration(duration: number): void {
    if (this.state.timeLimited && duration > 0) {
      this.state.durationInSeconds = duration;
    }
  }

  actionSetFinalObjectScore(recordId: string, score: string) {
    const { widget } = this.state;

    if (!widget) {
      return;
    }
    widget.setFinalObjectScore(recordId, score);
  }

  actionSetOnlySelected(onlySelected: boolean): void {
    this.state.onlySelected = onlySelected;
  }

  actionSetSessionType(value: VotingSessionType): void {
    this.state.sessionType = value;
    if (value === VotingSessionType.Poker) {
      this.actionSetDurationUnit(null);
    }
  }

  actionSetPokerPreset(preset: PokerPreset): void {
    this.state.pokerPreset = preset;
  }

  actionSetPokerChoices(choices: string): void {
    this.state.pokerChoices = choices.split(',').map(choice => choice.trim());
  }

  actionCloseVotingOnObject(objectId: string) {
    const { widget } = this.state;
    if (!widget) {
      return;
    }

    this.widgetsController.actionUpdateWidget(widget, {
      currentVotingObject: { recordId: objectId, votingStatus: 'finished' },
    });

    window.AhaAnalytics?.track(
      'Feature-7363417093450527106',
      'User reveals planning poker round'
    );
  }

  actionRestartVoting(objectId: string) {
    const { widget } = this.state;
    if (!widget) {
      return;
    }

    this.widgetsController.actionUpdateWidget(widget, {
      currentVotingObject: { recordId: objectId, votingStatus: 'started' },
    });

    window.AhaAnalytics?.track(
      'Feature-7363417093450527106',
      'User re-estimates planning poker round'
    );
  }

  actionSetSelectedObject(objectId: string | null) {
    const { widget } = this.state;

    if (!widget) {
      return;
    }
    if (objectId) {
      this.widgetsController.actionUpdateWidget(widget, {
        votableObjects: { [objectId]: true },
        currentVotingObject: { recordId: objectId, votingStatus: 'started' },
      });
    } else {
      this.state.selectedObjects = [];
      this.widgetsController.actionUpdateWidget(widget, {
        currentVotingObject: null,
        previousVotingObject: widget.currentVotingObject?.recordId,
      });
    }
  }

  actionSetVotesForObject(objectId: string, count: number): void {
    if (!this.userId) return;
    const widget = this.state.widget;
    if (!widget) return;

    widget.setVotesForObject(objectId, this.userId, count);
  }

  actionStartVotingSession(): void {
    const data: Pick<
      SerializedVotingSessionWidget,
      'name' | 'status' | 'startedAt' | 'sessionType'
    > &
      Partial<
        Pick<
          SerializedVotingSessionWidget,
          'votesPerPerson' | 'exactDuration' | 'votableObjects' | 'pokerChoices'
        >
      > = {
      name: this.name,
      status: VotingSessionStatus.Active,
      startedAt: Date.now(),
      sessionType: this.state.sessionType,
    };

    if (this.enabledFields.votesLimited && this.state.votesLimited) {
      data.votesPerPerson = this.state.votesPerPerson;
    }

    if (this.state.timeLimited) {
      data.exactDuration = timeInMillis(this.state.durationInSeconds);
    }

    if (this.state.onlySelected) {
      data.votableObjects = Object.fromEntries(
        this.state.selectedObjects.map(o => [o, true])
      );
    }

    if (this.state.sessionType === VotingSessionType.Poker) {
      data.pokerChoices = this.state.pokerChoices;
    }

    if (!this.state.widget) {
      const widget = this.widgetsController.actionAddWidget(
        'VotingSessionWidget',
        data
      ) as VotingSessionWidget;
      this.state.widget = widget;
      this.widgetsController.whiteboardController.canvas.requestRenderAll();
    }

    const localStorageData: z.infer<typeof zLocalStorageSettings> = {
      sessionType: data.sessionType,
    };
    if (data.sessionType === VotingSessionType.Poker) {
      localStorageData.pokerChoices = data.pokerChoices;
      localStorageData.pokerPreset = this.state.pokerPreset;
    }
    localStorage.setItem(
      localStorageLastSelectedVotingSessionType,
      JSON.stringify(localStorageData)
    );

    if (this.state.sessionType === VotingSessionType.Poker) {
      window.AhaAnalytics?.track(
        'Feature-7363417093450527106',
        'User starts planning poker session',
        {
          Units: this.state.pokerPreset,
        }
      );
    } else {
      window.AhaAnalytics?.track(
        'Feature-7236747227355825679',
        'User starts voting session',
        {
          'Timed voting session': this.state.timeLimited,
          'Vote limit per user': this.state.votesPerPerson,
          'Vote on selected objects': this.state.onlySelected,
        }
      );
    }
  }

  actionExtendVotingSession(timeInSeconds: number): void {
    const widget = this.state.widget;
    if (!widget?.exactDuration) return;

    this.widgetsController.actionUpdateWidget(widget, {
      exactDuration: widget.exactDuration + timeInSeconds * 1000,
    });
  }

  actionStopVotingSession(): void {
    const { widget } = this.state;
    if (!widget) return;

    const startedAt = widget.startedAt ?? Date.now();
    const exactDuration = Date.now() - startedAt;

    this.widgetsController.actionUpdateWidget(widget, {
      startedAt,
      exactDuration,
    });

    if (this.state.sessionType === VotingSessionType.Poker) {
      window.AhaAnalytics?.track(
        'Feature-7363417093450527106',
        'User ends planning poker session'
      );
    } else {
      window.AhaAnalytics?.track(
        'Feature-7236747227355825679',
        'User ends voting session',
        {}
      );
    }

    this.actionPushSidePanel({
      id: 'votingResults',
      name: widget.name,
      icon: 'aha-icon aha-icon-voting',
      panel: (
        <ControlledComponent controller={this}>
          <ResultsPanel widget={widget} />
        </ControlledComponent>
      ),
    });
  }

  actionRemoveVotingSession(widget: VotingSessionWidget): void {
    this.widgetsController.actionRemoveWidget(widget);
    window.AhaAnalytics?.track(
      'Feature-7236747227355825679',
      'User deletes voting session',
      {}
    );
  }

  actionFinishVoting() {
    if (!this.state.widget) return;
    if (!this.userId) return;
    if (this.finishedVoting) return;

    this.state.widget.markUserAsFinished(this.userId);
    this.widgetsController.whiteboardController.canvas.requestRenderAll();
  }

  actionPushSidePanel(item: SidePanelItem): void {
    const sidebarController = this.widgetsController.whiteboardController
      ?.sidePanelController as SidePanelController | undefined;

    if (sidebarController) {
      sidebarController.actionOpenSidePanel('voting');
      sidebarController.actionPushSidePanel(item);

      if (item.id === 'votingResults') {
        window.AhaAnalytics?.track(
          'Feature-7236747227355825679',
          'User views voting session results',
          {}
        );
      }
    }
  }

  actionPopSidePanel(): void {
    const sidebarController =
      this.widgetsController.whiteboardController?.sidePanelController;

    if (sidebarController) {
      sidebarController.actionPopSidePanel();
    }
  }

  getShapeByRecordId(recordId: string) {
    const shape =
      this.widgetsController.whiteboardController.canvas.findFirstObject(
        (obj): obj is Shape => obj.id === recordId
      );
    if (!shape) {
      return null;
    }
    return shape;
  }
}

export default VotingSessionController;
